import { cn } from '../../lib/utils';
import { ButtonHTMLAttributes, forwardRef } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={cn(
          'inline-flex items-center justify-center rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed',
          {
            'bg-primary text-white hover:bg-primary-dark': variant === 'primary',
            'bg-secondary text-white hover:bg-secondary/90': variant === 'secondary',
            'border border-border bg-surface text-text hover:bg-background': variant === 'outline',
            'text-text-muted hover:text-text hover:bg-background': variant === 'ghost',
            'bg-danger text-white hover:bg-danger/90': variant === 'danger',
            // Explicit min-heights keep icon-only buttons the same height as
            // text buttons of the same size (an icon has a shorter line box
            // than text, so it would otherwise collapse).
            'px-3 py-1.5 text-sm min-h-8': size === 'sm',
            'px-4 py-2 text-sm min-h-9': size === 'md',
            'px-6 py-3 text-base min-h-12': size === 'lg',
          },
          className,
        )}
        {...props}
      >
        {isLoading ? (
          <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : null}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
